"use client";

import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import {
  Box,
  Grid,
  TextField,
  Button,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  SelectChangeEvent,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import SelectCaret from "@/src/components/common/broadsheet/select-caret";
import { useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { isValidDate } from "@/src/utils/is-valid-date";
import Kicker from "@/src/components/common/broadsheet/kicker";
import { fieldSx } from "@/src/components/common/broadsheet/form-kit";
import {
  actionSx,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";

interface FiltersProps {
  currentJournal?: any;
  journals: Journal[];
  filters: {
    journal: string[];
    startDate?: Date;
    endDate?: Date;
    volume: string;
    issue: string;
  };
  sortBy: string;
  onFilterChange: (
    filterName: keyof FiltersProps["filters"],
    value: any,
  ) => void;
  onSortByChange: (value: string) => void;
  onResetFilters: () => void;
}

interface JournalProps {
  id: number;
  value: number;
  label: string;
}

const Filters = ({
  currentJournal,
  journals,
  filters,
  onFilterChange,
  onSortByChange,
  onResetFilters,
}: FiltersProps) => {
  const initialized = useRef<boolean>(false);
  const isUpdatingUrl = useRef<boolean>(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const initialVolume = searchParams.get("filter[volume]") || "";
  const initialIssue = searchParams.get("filter[issue]") || "";
  const initialJournalIds =
    searchParams.get("filter[journal_id]")?.split(",") ?? [];
  const initialSortBy = searchParams.get("sort_by") || "none";
  const journalsList: JournalProps[] = journals.map((journal: Journal) => ({
    id: journal.id,
    value: journal.id,
    label: journal.title,
  }));

  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean | undefined>(
    undefined,
  );
  const [volume, setVolume] = useState(initialVolume);
  const [issue, setIssue] = useState(initialIssue);
  const [sortBy, setSortBy] = useState(initialSortBy);
  const [selectedJournals, setSelectedJournals] = useState<JournalProps[]>(
    () => {
      const urlJournals = journalsList.filter((journal: JournalProps) =>
        initialJournalIds.includes(String(journal.id)),
      );

      if (
        currentJournal &&
        !urlJournals.find((journal) => journal.id === currentJournal.id)
      ) {
        return [...urlJournals, currentJournal];
      }

      return urlJournals;
    },
  );

  const [shouldShowAdvancedFilters, setShouldShowAdvancedFilters] =
    useState<boolean>(volume !== "" || issue !== "");

  const hasActiveFilters =
    filters.journal.length > 0 ||
    filters.startDate ||
    filters.endDate ||
    filters.volume ||
    filters.issue;

  useEffect(() => {
    if (!initialized.current && currentJournal) {
      setSelectedJournals((prev) => {
        const isAlreadySelected = prev.find(
          (journal) => journal.id === currentJournal.id,
        );

        if (!isAlreadySelected) {
          const newSelection = [...prev, currentJournal];

          setTimeout(() => {
            const ids = newSelection.map((journal) => String(journal.id));
            onFilterChange("journal", ids);
            updateUrl("filter[journal_id]", ids);
          }, 0);

          return newSelection;
        }

        return prev;
      });

      initialized.current = true;
    }
  }, [currentJournal]);

  useEffect(() => {
    if (isAdvancedOpen !== undefined) {
      setShouldShowAdvancedFilters(isAdvancedOpen);
    }
  }, [isAdvancedOpen]);

  useEffect(() => {
    if (isUpdatingUrl.current) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());

    const journalIds = params.get("filter[journal_id]");
    if (journalIds && !currentJournal) {
      onFilterChange("journal", journalIds.split(","));
    }

    const dateFrom = params.get("filter[date_from]");
    if (dateFrom) {
      const parsed = new Date(dateFrom);
      if (isValidDate(parsed)) {
        onFilterChange("startDate", parsed);
      }
    }

    const dateTo = params.get("filter[date_to]");
    if (dateTo) {
      const parsed = new Date(dateTo);
      if (isValidDate(parsed)) {
        onFilterChange("endDate", parsed);
      }
    }

    const volume = params.get("filter[volume]");
    if (volume) {
      onFilterChange("volume", volume);
    }

    const issue = params.get("filter[issue]");
    if (issue) {
      onFilterChange("issue", issue);
    }

    const sort = params.get("sort_by");
    if (sort) {
      onSortByChange(sort);
    }
  }, []);

  const handleJournalChange = (_: React.BaseSyntheticEvent, newValue: any) => {
    const ids = newValue.map((journal: any) => String(journal.id));
    setSelectedJournals(newValue);
    onFilterChange("journal", ids);
    updateUrl("filter[journal_id]", ids);
  };

  const handleSortChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSortBy(value);
    onSortByChange(value);
    updateUrl("sort_by", value);
  };

  const handleVolumeChange = (event: React.BaseSyntheticEvent) => {
    const value = event.target.value;
    setVolume(value);
    onFilterChange("volume", value);
    updateUrl("filter[volume]", value);
  };

  const handleIssueChange = (event: React.BaseSyntheticEvent) => {
    const value = event.target.value;
    setIssue(value);
    onFilterChange("issue", value);
    updateUrl("filter[issue]", value);
  };

  const handleStartDateChange = (date: Date | null) => {
    const validDate = isValidDate(date) ? date : undefined;
    onFilterChange("startDate", validDate);
    updateUrl(
      "filter[date_from]",
      validDate ? validDate.toISOString().split("T")[0] : undefined,
    );
  };

  const handleEndDateChange = (date: Date | null) => {
    const validDate = isValidDate(date) ? date : undefined;
    onFilterChange("endDate", validDate);
    updateUrl(
      "filter[date_to]",
      validDate ? validDate.toISOString().split("T")[0] : undefined,
    );
  };

  const handleReset = () => {
    const params = new URLSearchParams(window.location.search);

    const filtersToRest = [
      "filter[journal_id]",
      "sort_by",
      "filter[volume]",
      "filter[issue]",
      "filter[date_from]",
      "filter[date_to]",
    ];

    filtersToRest.forEach((key) => params.delete(key));

    const newUrl = `${window.location.pathname}?${params.toString()}`;

    isUpdatingUrl.current = true;
    window.history.replaceState(null, "", newUrl);

    setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 100);

    onResetFilters();
    setVolume("");
    setIssue("");
    setSortBy("none");
    setSelectedJournals([]);
  };

  const updateUrl = (key: string, value?: string | string[]) => {
    const params = new URLSearchParams(searchParams.toString());

    if (!value || (Array.isArray(value) && value.length === 0)) {
      params.delete(key);
    } else {
      params.set(key, Array.isArray(value) ? value.join(",") : value);
    }

    const query = params.toString();
    const newUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;

    if (`${window.location.pathname}${window.location.search}` === newUrl) {
      return;
    }

    isUpdatingUrl.current = true;
    router.replace(newUrl);

    setTimeout(() => {
      isUpdatingUrl.current = false;
    }, 100);
  };

  return (
    <Box sx={{ py: { xs: 1.25, md: 1.5 }, ...hairline }}>
      <Kicker component="p" sx={{ mb: 0.5 }}>
        {hasActiveFilters ? "Filters — active" : "Filters"}
      </Kicker>
      <Grid container spacing={{ xs: 0, md: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Autocomplete
            multiple
            size="small"
            options={journalsList}
            value={selectedJournals}
            onChange={handleJournalChange}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            filterSelectedOptions
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Filter by journals"
                placeholder="Select journals"
                fullWidth
                sx={fieldSx}
              />
            )}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => (
                <Chip
                  {...getItemProps({ index })}
                  key={option.id}
                  label={option.label}
                  size="small"
                  color="primary"
                  variant="filled"
                  sx={{ borderRadius: 0 }}
                />
              ))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small" margin="normal" sx={fieldSx}>
            <InputLabel id="search-sort-by-label">Sort by</InputLabel>
            <Select
              labelId="search-sort-by-label"
              id="search-sort-by"
              value={sortBy || "none"}
              onChange={handleSortChange}
              label="Sort by"
            >
              <MenuItem value="none">Relevance</MenuItem>
              <MenuItem value="-date">Date: newest first</MenuItem>
              <MenuItem value="+date">Date: oldest first</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box sx={{ mt: 0.5 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
          aria-expanded={!!shouldShowAdvancedFilters}
          sx={{
            border: 0,
            p: 0,
            background: "none",
            cursor: "pointer",
            fontFamily: FONT_UI,
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "primary.main",
            textDecoration: "underline",
            textUnderlineOffset: "3px",
          }}
        >
          {isAdvancedOpen ? "Hide" : "Show"} advanced filters
        </Box>

        <Collapse in={shouldShowAdvancedFilters}>
          <Box
            sx={{
              mt: 0.5,
              p: 1,
              border: "1px dashed",
              borderColor: "custom.ruleHairline",
              borderRadius: 0,
              backgroundColor: "custom.newsprintRaised",
            }}
          >
            <Grid container spacing={{ xs: 0, md: 2 }}>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  margin="normal"
                  label="Volume"
                  placeholder="Volume"
                  value={filters.volume || ""}
                  onChange={handleVolumeChange}
                  slotProps={{ htmlInput: { min: 0 } }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  margin="normal"
                  label="Issue"
                  placeholder="Issue"
                  value={filters.issue || ""}
                  onChange={handleIssueChange}
                  slotProps={{ htmlInput: { min: 0 } }}
                  sx={fieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Published from"
                    value={filters.startDate || null}
                    onChange={handleStartDateChange}
                    slots={{ openPickerIcon: SelectCaret }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        margin: "normal",
                        sx: fieldSx,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Published to"
                    value={filters.endDate || null}
                    onChange={handleEndDateChange}
                    slots={{ openPickerIcon: SelectCaret }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        margin: "normal",
                        sx: fieldSx,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>

      <Box
        sx={{
          mt: 0.75,
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            fontFamily: FONT_UI,
            fontSize: "0.71875rem",
            color: "custom.inkMuted",
          }}
        >
          {hasActiveFilters
            ? `${[
                filters.journal.length > 0 &&
                  `${filters.journal.length} journal${filters.journal.length > 1 ? "s" : ""}`,
                filters.startDate && "start date",
                filters.endDate && "end date",
                filters.volume && "volume",
                filters.issue && "issue",
              ]
                .filter(Boolean)
                .join(", ")} selected`
            : ""}
        </Box>
        <Button
          type="button"
          onClick={handleReset}
          disabled={!hasActiveFilters}
          sx={{
            ...actionSx("outline", "small"),
            "&.Mui-disabled": {
              color: "custom.subtitleText",
              borderColor: "custom.ruleHairline",
            },
          }}
        >
          Reset all filters
        </Button>
      </Box>
    </Box>
  );
};

export default Filters;
